import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCircle2, Pencil, Check, X } from "lucide-react";
import api from "@/lib/api";

/**
 * Badge + botão para reatribuir o preparador da Estimativa.
 *
 * - Se ninguém preparou ainda: não renderiza (a auto-atribuição
 *   acontece no /estimate/generate).
 * - Se está atribuído: mostra "Preparado por X · dd/mm".
 * - Se o utilizador actual pode editar (admin/gestor sempre;
 *   restantes só podem atribuir a si próprios via botão "Sou eu"):
 *   mostra ícone de lápis que abre o selector.
 */
export default function EstimatePreparerBadge({ lead, currentUser, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState("");

  const preparedById = lead?.estimate_prepared_by_id;
  const preparedByName = lead?.estimate_prepared_by_name;
  const preparedAt = lead?.estimate_prepared_at;

  const canReassignAny =
    currentUser?.role === "admin" || currentUser?.role === "gestor";
  const canSelfAssign =
    ["admin", "gestor", "atendimento", "consultor"].includes(currentUser?.role);

  useEffect(() => {
    if (!editing || !canReassignAny) return;
    (async () => {
      try {
        const { data } = await api.get("/users");
        const list = Array.isArray(data) ? data : data.items || [];
        setUsers(
          list
            .filter((u) => u.active !== false)
            .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        );
      } catch (e) {
        toast.error("Erro ao carregar utilizadores.");
      }
    })();
  }, [editing, canReassignAny]);

  const assign = async (targetId) => {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/leads/${lead.id}/estimate/set-preparer`,
        { user_id: targetId },
      );
      toast.success(`Preparador actualizado: ${data.estimate_prepared_by_name}`);
      setEditing(false);
      setSelected("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao reatribuir.");
    } finally {
      setSaving(false);
    }
  };

  // Sem estimativa gerada — nada para mostrar
  if (!lead?.estimate?.options?.length) return null;

  return (
    <div
      className="inline-flex items-center gap-2 text-xs text-muted-foreground"
      data-testid="estimate-preparer-badge"
    >
      <UserCircle2 className="w-3.5 h-3.5 text-muted-foreground" />
      {preparedById ? (
        <>
          <span>
            Preparada por <b className="text-foreground">{preparedByName || "—"}</b>
            {preparedAt && (
              <span className="text-muted-foreground ml-1">
                · {new Date(preparedAt).toLocaleDateString("pt-PT")}
              </span>
            )}
          </span>
          {canSelfAssign && !editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid="estimate-preparer-edit-btn"
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              title="Reatribuir preparador"
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </>
      ) : (
        <>
          <span className="text-muted-foreground italic">Sem preparador registado</span>
          {canSelfAssign && !editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid="estimate-preparer-edit-btn"
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              title="Atribuir preparador"
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={(e) => e.target === e.currentTarget && setEditing(false)}
        >
          <div className="bg-card rounded-lg shadow-xl border border-border max-w-md w-full p-5">
            <h3 className="text-base font-semibold text-foreground mb-1">
              Reatribuir preparador da estimativa
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              O nome aparece na capa do PDF e no email. Escolhe quem preparou
              este orçamento.
            </p>

            {/* Quick "Sou eu" */}
            <button
              type="button"
              onClick={() => assign(currentUser.id)}
              disabled={saving || preparedById === currentUser?.id}
              data-testid="estimate-preparer-self-btn"
              className="w-full mb-3 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] hover:bg-[hsl(var(--success-bg))] text-sm font-semibold text-[hsl(var(--success))] disabled:opacity-40"
            >
              <Check className="w-3.5 h-3.5" /> Fui eu ({currentUser?.name})
            </button>

            {canReassignAny ? (
              <>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Ou escolhe outro utilizador
                </label>
                <select
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  data-testid="estimate-preparer-select"
                  className="block w-full rounded-md border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/20"
                >
                  <option value="">— seleccionar —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.role})
                    </option>
                  ))}
                </select>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="px-4 py-2 rounded-md border border-border text-sm font-medium bg-card hover:bg-muted/40"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => selected && assign(selected)}
                    disabled={!selected || saving}
                    data-testid="estimate-preparer-confirm-btn"
                    className="px-4 py-2 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold disabled:opacity-40"
                  >
                    {saving ? "A guardar…" : "Atribuir"}
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-2 text-xs text-muted-foreground">
                Só admin ou gestor podem atribuir a autoria a outro
                utilizador. Podes atribuir a ti próprio no botão acima.
              </div>
            )}

            <button
              type="button"
              onClick={() => setEditing(false)}
              className="absolute top-3 right-3 p-1 rounded hover:bg-muted text-muted-foreground hover:text-muted-foreground"
              aria-label="Fechar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
